import { useCallback, useEffect, useId, useRef, useState } from 'react'

import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import {
  IntradayBasis,
  IntradayPriceMode,
} from '@ebalance/proto/bffuser/v1/approval_flow_model_pb'
import { useTranslation } from 'react-i18next'

import {
  Input,
  PrimaryButton,
  RadioButton,
  TertiaryButton,
  Text,
  fontSizes,
} from '@enechain/ecloud-designsystem'

import { ButtonTooltip } from '~/components/common/ButtonTooltip'
import { DsSelect, type DsSelectOptions } from '~/components/common/DsSelect'
import { enumSetValueAs } from '~/components/common/EnumSelect'
import { Modal } from '~/components/common/Modal'
import { TradeSideLabel } from '~/components/common/TradeSideLabel'
import { convertAreaLabel } from '~/models/area'
import { AutoBidFormRow } from '~/pages/AdminApprovalFlow/components/AutoBidFormRow'
import { AutoBidPriceRangeHint } from '~/pages/AdminApprovalFlow/components/AutoBidPriceRangeHint'
import {
  AUTO_BID_INTRADAY_DRAFT_ERROR_MESSAGE_KEY,
  AUTO_BID_VALIDATION_MESSAGE_KEY,
} from '~/pages/AdminApprovalFlow/constants/autoBidValidationMessageKey'
import { eclColor } from '~/pages/AdminApprovalFlow/constants/eclColor'
import { AREAS_JEPX } from '~/pages/AdminApprovalFlow/constants/processMap'
import {
  AUTO_BID_MAX_ADJUSTMENT,
  AUTO_BID_MAX_PRICE,
  AUTO_BID_MIN_ADJUSTMENT,
  AUTO_BID_MIN_PRICE,
  AUTO_BID_SPOT_REFERENCE_SAME_AS_TARGET,
  type AutoBidFeeAutoConsider,
  type AutoBidIntradayDraftError,
  type AutoBidIntradaySideDraft,
  type AutoBidIntradaySubmission,
  type AutoBidPriceRange,
  type AutoBidValidationError,
  computeAutoBidIntradayEffective,
  firstAutoBidIntradayError,
  intradayBestQuoteLabel,
  isAutoBidFeeAutoConsiderOn,
  parseAutoBidIntradayDraft,
  validateAutoBidIntradaySide,
} from '~/pages/AdminApprovalFlow/models/autoBid'

type SideKind = 'buy' | 'sell'

export type Props = {
  isOpen: boolean
  onClose: () => void
  title: string
  subtitle?: string
  initialBuy: AutoBidIntradaySideDraft
  initialSell: AutoBidIntradaySideDraft
  /** 入札制限 (時間前) の価格上下限。 一括モーダルは上下限なし。 */
  buyPriceRange: AutoBidPriceRange
  sellPriceRange: AutoBidPriceRange
  /** 数量上限 (BidKind.QTY、 MW)。 undefined = 制限なし。 */
  buyQuantityLimit: number | undefined
  sellQuantityLimit: number | undefined
  /** 手数料自動考慮 (時間前)。 欄名と「→ 入札価格」の加減算に使う (AC-APF-042 / 043)。 */
  fee: AutoBidFeeAutoConsider
  /** 全エリア一括編集か。 参照エリアに「対象エリアと同じ」を出す (AC-APF-039)。 */
  isBulk?: boolean
  onSave: (
    buy: AutoBidIntradaySubmission,
    sell: AutoBidIntradaySubmission,
  ) => void
}

/**
 * 時間前入札の自動入札ルール編集モーダル (design AC-02/AC-03/AC-04/AC-07)。
 *
 * 買 (不足時) / 売 (余剰時) の 2 列を独立に編集する。 priceMode に応じて
 * 表示項目が切り替わる (design 画面状態・挙動マトリクス):
 *   - 価格を決める, basis≠固定: 基準 + 調整 (固定価格・対向参照・入札価格は非表示)
 *   - 価格を決める, basis=固定: 基準 + 固定価格 + 調整 (対向参照・入札価格は非表示)
 *   - 食いつき: 対向参照価格 + 入札価格 + 調整 (基準・固定価格は非表示)
 *
 * isOpen が true になる度に initialBuy/initialSell へリセットするため、
 * 「キャンセル」はモーダルを開く直前の状態へロールバックする (AC-07)。
 *
 * 検証は 2 方式が並ぶ。 範囲エラーは保存を先に無効化し、 基準の未選択
 * (AC-APF-076) は「保存」 を押した時に拒否して基準セレクトの下に出す。
 */
export const AutoBidIntradayEditModal = ({
  isOpen,
  onClose,
  title,
  subtitle,
  initialBuy,
  initialSell,
  buyPriceRange,
  sellPriceRange,
  buyQuantityLimit,
  sellQuantityLimit,
  fee,
  isBulk = false,
  onSave,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const [buy, setBuy] = useState<AutoBidIntradaySideDraft>(initialBuy)
  const [sell, setSell] = useState<AutoBidIntradaySideDraft>(initialSell)
  const [submitAttempted, setSubmitAttempted] = useState(false)
  const buyBasisInputRef = useRef<HTMLInputElement | null>(null)
  const sellBasisInputRef = useRef<HTMLInputElement | null>(null)
  const setBuyBasisInput = useCallback((el: HTMLInputElement | null) => {
    buyBasisInputRef.current = el
  }, [])
  const setSellBasisInput = useCallback((el: HTMLInputElement | null) => {
    sellBasisInputRef.current = el
  }, [])

  // AC-07: モーダルを開く「瞬間」(閉→開の立ち上がり) のルール状態へロール
  // バックする。 AutoBidEditModal と同じ立ち上がりエッジ検出パターン
  // (親再レンダーで編集内容を保持する理由も同様)。
  // 各欄が持つ表示用の下書き文字列 (DraftNumberInput) もモーダルを開く度に
  // 破棄するため、 開いた回数を key にして side form を作り直す。
  const [openSeq, setOpenSeq] = useState(0)
  const prevIsOpenRef = useRef(false)
  useEffect(() => {
    if (isOpen && !prevIsOpenRef.current) {
      setBuy(initialBuy)
      setSell(initialSell)
      setSubmitAttempted(false)
      setOpenSeq((n) => n + 1)
    }
    prevIsOpenRef.current = isOpen
  }, [isOpen, initialBuy, initialSell])

  const buyParse = parseAutoBidIntradayDraft(buy)
  const sellParse = parseAutoBidIntradayDraft(sell)
  const handleSave = (): void => {
    if (buyParse.ok && sellParse.ok) {
      onSave(buyParse.submission, sellParse.submission)
      return
    }
    setSubmitAttempted(true)
    const firstInvalid = !buyParse.ok ? buyBasisInputRef : sellBasisInputRef
    firstInvalid.current?.focus()
  }

  // 範囲エラーは各入力欄の直下にインライン表示する (design/CLAUDE.md
  // 「入力エラーはインライン、 toast にしない」)。 保存は事前に無効化し
  // (spec AC-APF-033/034 の「保存を拒否する」)、 押せない理由は title で示す
  // (先行例: AdminBidLimits の BidLimitMatrixCard)。
  const firstError =
    firstAutoBidIntradayError(buy) ?? firstAutoBidIntradayError(sell)

  return (
    <Modal
      footer={
        <div
          style={{
            display: 'flex',
            width: '100%',
            justifyContent: 'flex-end',
            gap: '8px',
          }}
        >
          {/* design の自動入札ルール modal footer は `.btn`(40px) = md。 */}
          <TertiaryButton onClick={onClose} size="md">
            {t('page:adminApprovalFlow.actions.cancel')}
          </TertiaryButton>
          <ButtonTooltip
            content={
              firstError !== undefined
                ? t(AUTO_BID_VALIDATION_MESSAGE_KEY[firstError])
                : undefined
            }
          >
            <PrimaryButton
              disabled={firstError !== undefined}
              onClick={handleSave}
              size="md"
            >
              {t('page:adminApprovalFlow.actions.save')}
            </PrimaryButton>
          </ButtonTooltip>
        </div>
      }
      isOpen={isOpen}
      onClose={onClose}
      scrollBehavior="inside"
      size="lg"
      title={title}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {subtitle !== undefined && subtitle !== '' && (
          <div
            style={{ fontSize: fontSizes.md.value, color: eclColor.textMid }}
          >
            {subtitle}
          </div>
        )}
        <AutoBidIntradaySideForm
          key={`buy-${openSeq}`}
          basisError={
            submitAttempted && !buyParse.ok ? buyParse.error : undefined
          }
          basisInputRef={setBuyBasisInput}
          fee={fee}
          isBulk={isBulk}
          onChange={setBuy}
          priceRange={buyPriceRange}
          quantityLimit={buyQuantityLimit}
          side="buy"
          value={buy}
        />
        <AutoBidIntradaySideForm
          key={`sell-${openSeq}`}
          basisError={
            submitAttempted && !sellParse.ok ? sellParse.error : undefined
          }
          basisInputRef={setSellBasisInput}
          fee={fee}
          isBulk={isBulk}
          onChange={setSell}
          priceRange={sellPriceRange}
          quantityLimit={sellQuantityLimit}
          side="sell"
          value={sell}
        />
      </div>
    </Modal>
  )
}

type AutoBidIntradaySideFormProps = {
  side: SideKind
  value: AutoBidIntradaySideDraft
  priceRange: AutoBidPriceRange
  quantityLimit: number | undefined
  fee: AutoBidFeeAutoConsider
  isBulk: boolean
  /** 「保存」 を押した後の基準のエラー (押す前は undefined)。 */
  basisError: AutoBidIntradayDraftError | undefined
  basisInputRef: (el: HTMLInputElement | null) => void
  onChange: (v: AutoBidIntradaySideDraft) => void
}

const AutoBidIntradaySideForm = ({
  side,
  value,
  priceRange,
  fee,
  isBulk,
  quantityLimit,
  basisError,
  basisInputRef,
  onChange,
}: AutoBidIntradaySideFormProps): React.ReactElement => {
  const { t } = useTranslation()
  const basisErrorId = useId()
  const sideLabel = t(`page:adminApprovalFlow.autoBid.side.${side}Label`)

  const handleCoverageChange = (n: number): void => {
    onChange({ ...value, coveragePct: n })
  }

  const handleNumberChange = (
    field: keyof Pick<
      AutoBidIntradaySideDraft,
      | 'fixedPriceYenPerKwh'
      | 'adjustmentYenPerKwh'
      | 'counterRefPriceYenPerKwh'
      | 'bidPriceYenPerKwh'
    >,
    n: number,
  ): void => {
    onChange({ ...value, [field]: n })
  }

  const isDetermine = value.priceMode === IntradayPriceMode.DETERMINE
  const isSnapUp = value.priceMode === IntradayPriceMode.SNAP_UP
  const isFixedBasis = isDetermine && value.basis === IntradayBasis.FIXED
  const isSpotBasis =
    isDetermine && value.basis === IntradayBasis.SPOT_AREA_PRICE

  // 基準の 4 択 (spec AC-APF-042 / 8080 `js/autobid-rule.js`)。
  // 順序は spec の記載順そのまま。 `DsSelect` は value を `String()` して
  // option と突き合わせるため、 enum 値も文字列で持たせる。
  const basisOptions: DsSelectOptions = [
    {
      value: String(IntradayBasis.BEST_QUOTE),
      label: intradayBestQuoteLabel(t, side),
    },
    {
      value: String(IntradayBasis.LATEST_TRADE),
      label: t(
        'page:adminApprovalFlow.autoBid.modal.intraday.basisLatestTrade',
      ),
    },
    {
      value: String(IntradayBasis.SPOT_AREA_PRICE),
      label: t(
        'page:adminApprovalFlow.autoBid.modal.intraday.basisSpotAreaPrice',
      ),
    },
    {
      value: String(IntradayBasis.FIXED),
      label: t('page:adminApprovalFlow.autoBid.modal.intraday.basisFixed'),
    },
  ]

  // 参照エリア (沖縄を除く 9 エリア、 AC-APF-042)。 一括編集だけ先頭に
  // 「対象エリアと同じ」を置く (AC-APF-039)。
  const spotReferenceOptions: DsSelectOptions = [
    ...(isBulk
      ? [
          {
            value: AUTO_BID_SPOT_REFERENCE_SAME_AS_TARGET,
            label: t(
              'page:adminApprovalFlow.autoBid.modal.intraday.spotReferenceSameAsTarget',
            ),
          },
        ]
      : []),
    ...AREAS_JEPX.map((area) => ({
      value: String(area),
      label: convertAreaLabel(area),
    })),
  ]

  // 表示 (= 送信) される項目だけの範囲エラー。 非表示の項目は payload にも
  // 載らないため検証しない (models 側で priceMode/basis を見て分岐)。
  const errors = validateAutoBidIntradaySide(value)
  // 価格の実効値は価格を直接入力する欄 (固定価格 / 入札価格) にだけ出す。
  // 動的基準では undefined になり、 上下限だけを出す (AC-APF-043)。
  const effective = computeAutoBidIntradayEffective(
    value,
    side,
    priceRange,
    fee,
  )
  // 手数料自動考慮 ON の価格欄は希望価格 (対向参照価格は加減算しないので変えない。 AC-APF-042)
  const feeOn = isAutoBidFeeAutoConsiderOn(fee)

  return (
    <div
      style={{
        border: `1px solid ${eclColor.borderLow}`,
        // eslint-disable-next-line no-restricted-syntax -- D-2 ベースライン (AutoBidEditModal 踏襲、D-1 の系統 1 本化で解消予定)
        borderRadius: '6px',
        padding: '16px',
        background: eclColor.surfaceSecondary,
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
      }}
    >
      {/* design `.apr-abform-side-title` (approval-flow.html:371) は
          font-size `--font-size-md` = 14px / weight 700 / gap `--space-2` = 8px。
          実装は 16px (lg) / 600 で 1 段大きく細かった (2026-09-04 デザイナー指摘)。 */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontWeight: 700,
          fontSize: fontSizes.md.value,
          color: eclColor.textHigh,
        }}
      >
        {/* 買 / 売 は DS `TradeLabel` の色付き pill (design `.ecl-trade-label--buy`
            = #007d80 / `--sell` = #b52020)。 素の span + `Surface.tertiary` の
            灰色バッジだと**買いと売りが同じ見た目**になり、 左右どちらの列かが
            色で判別できなかった (2026-09-04 デザイナー指摘)。
            共通ラッパ `TradeSideLabel` は読み上げ名と EN の 1 文字表示も持つ。 */}
        <TradeSideLabel side={side === 'buy' ? Side.BUY : Side.SELL} />
        {sideLabel}
      </div>

      {/* AC-APF-043: 数量上限は 設定＞入札 が優先される旨を常時表示する。
          時間前の自動入札ルールに成行は無いので成行可否は出さない。 */}
      <div style={{ fontSize: fontSizes.sm.value, color: eclColor.textMid }}>
        {quantityLimit !== undefined
          ? t('page:adminApprovalFlow.autoBid.modal.quantityLimitFormat', {
              value: quantityLimit.toFixed(1),
            })
          : t('page:adminApprovalFlow.autoBid.modal.quantityLimitUnlimited')}
      </div>

      {/* スポット側 (`AutoBidEditModal`) と同じ `AutoBidFormRow` に載せる。
          素の `<span>` + `aria-label` だとラベル列の幅が文字数で変わり、
          行ごとに入力の左端がずれる (design `.apr-abform-label` は
          `flex: 0 0 124px` の固定列)。 2026-09-14 デザイナー指摘
          「自動入札ルール modal の UI 構造をスポットと時間前で揃えたい」。 */}
      <AutoBidFormRow
        label={t('page:adminApprovalFlow.autoBid.modal.coverage')}
      >
        {({ controlId }) => (
          <>
            {/* `aria-label` は書かない。 関連付けは AutoBidFormRow が配る id が
                持つので、 二重に書くと文言がずれる余地だけが残る。 */}
            <DraftNumberInput
              clampToRange
              id={controlId}
              inputWidth="80px"
              max={100}
              min={0}
              onChangeNumber={handleCoverageChange}
              step={1}
              value={value.coveragePct}
            />
            <Text color="Text.midEmphasis">
              {t('page:adminApprovalFlow.autoBid.modal.coverageUnit')}
            </Text>
          </>
        )}
      </AutoBidFormRow>

      {/* 価格モードは DS `RadioButton` を使う。 生の `<input type="radio">` は OS
          既定の見た目になり、 design `.ecl-radio` (= DS radio) と揃わない
          (2026-09-03 デザイナー指摘)。 スポット側の「発注方法」は既に DS
          `RadioButton` で、 同じ役割の部品が 2 つの modal で割れていた。 */}
      <AutoBidFormRow
        label={t('page:adminApprovalFlow.autoBid.modal.intraday.priceMode')}
      >
        {({ labelId }) => (
          // 単一の control ではないので `htmlFor` ではなく `aria-labelledby` で結ぶ。
          <RadioButton
            aria-labelledby={labelId}
            name={`autobid-intraday-price-mode-${side}`}
            onValueChange={(details) => {
              const next = Number(details.value)
              if (!Number.isNaN(next)) {
                onChange({ ...value, priceMode: next })
              }
            }}
            radios={[
              {
                label: t(
                  'page:adminApprovalFlow.autoBid.modal.intraday.priceModeDetermine',
                ),
                value: String(IntradayPriceMode.DETERMINE),
              },
              {
                label: t(
                  'page:adminApprovalFlow.autoBid.modal.intraday.priceModeSnapUp',
                ),
                value: String(IntradayPriceMode.SNAP_UP),
              },
            ]}
            value={String(value.priceMode)}
          />
        )}
      </AutoBidFormRow>

      {isDetermine && (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <AutoBidFormRow
              label={t('page:adminApprovalFlow.autoBid.modal.intraday.basis')}
            >
              {({ controlId }) => (
                <>
                  {/* 単一 select の正は DS `Select` (`~/components/common/DsSelect`)。
                本画面の他の select (MatrixModeCell / ProcessAccordion / MatrixHeaders)
                は既に移行済みで、 ここだけ生 `<select>` が残っていた
                (= OS 既定の高さ・角丸・矢印になり、 隣の入力と揃わない)。

                size は指定しない (= DS 既定 md = 40px)。 design
                (`admin/approval-flow.html:1549` の `ecl-select ecl-select--sm
                apr-autobid-type`) が **40px** で、 `--sm` は名前に反して
                `height: 40px`、 font-size と padding だけを変える modifier
                (`css/components.css:3276`)。 `.apr-autobid-type` 側にも height の
                override は無い (同 html :350 は min-width のみ)。 隣の入力も
                同じ理由で 40px。 */}
                  <DsSelect
                    // DS `Select` (chakra-react-select) は枠・区切り・矢印で chrome を
                    // 約 91px 使う (MatrixModeCell の同 JSDoc 参照)。 native select と違い
                    // 最長 option へ自動追従しないので、 最長「最良気配」 (4 字 × 14px = 56px)
                    // + chrome 91px = 147 → 148px を明示し、 値を切り替えても幅が動かない
                    // ようにする (同画面の mode select と同値)。
                    minWidth="148px"
                    // `aria-label` は書かない。 DS `Select` は `id` を受けず
                    // react-select の `inputId` が本体なので、 Row が配る controlId を
                    // そこへ渡してラベルと結ぶ (前例: FormSelect.test.tsx
                    // 「inputId を渡すと sibling の label から引ける」)。
                    aria-describedby={
                      basisError !== undefined ? basisErrorId : undefined
                    }
                    inputId={controlId}
                    inputRef={basisInputRef}
                    invalid={basisError !== undefined}
                    onChange={(raw) => {
                      const basis = enumSetValueAs(raw)
                      if (basis !== undefined) onChange({ ...value, basis })
                    }}
                    options={basisOptions}
                    value={value.basis}
                  />
                </>
              )}
            </AutoBidFormRow>
            {basisError !== undefined && (
              <div
                id={basisErrorId}
                style={{
                  fontSize: fontSizes.sm.value,
                  color: eclColor.negative,
                }}
              >
                {t(AUTO_BID_INTRADAY_DRAFT_ERROR_MESSAGE_KEY[basisError])}
              </div>
            )}
          </div>

          {isSpotBasis && (
            <AutoBidFormRow
              label={t(
                'page:adminApprovalFlow.autoBid.modal.intraday.spotReferenceArea',
              )}
            >
              {({ controlId }) => (
                <DsSelect
                  // 最長「対象エリアと同じ」 (8 字 × 14px = 112px) + chrome 91px。
                  minWidth="204px"
                  inputId={controlId}
                  onChange={(raw) => {
                    if (raw === AUTO_BID_SPOT_REFERENCE_SAME_AS_TARGET) {
                      onChange({ ...value, spotReferenceArea: raw })
                      return
                    }
                    const area = enumSetValueAs(raw)
                    if (area !== undefined)
                      onChange({ ...value, spotReferenceArea: area })
                  }}
                  options={spotReferenceOptions}
                  value={value.spotReferenceArea}
                />
              )}
            </AutoBidFormRow>
          )}

          {isFixedBasis ? (
            <>
              <AutoBidIntradayNumberField
                error={errors.fixedPrice}
                label={
                  feeOn
                    ? t(
                        'page:adminApprovalFlow.autoBid.modal.intraday.fixedDesiredPrice',
                      )
                    : t(
                        'page:adminApprovalFlow.autoBid.modal.intraday.fixedPrice',
                      )
                }
                max={AUTO_BID_MAX_PRICE}
                min={AUTO_BID_MIN_PRICE}
                onChangeNumber={(n) =>
                  handleNumberChange('fixedPriceYenPerKwh', n)
                }
                value={value.fixedPriceYenPerKwh}
              />
              <AutoBidPriceRangeHint
                effective={effective}
                priceRange={priceRange}
              />
            </>
          ) : (
            <AutoBidPriceRangeHint
              effective={undefined}
              priceRange={priceRange}
            />
          )}
        </>
      )}

      {isSnapUp && (
        <>
          <AutoBidIntradayNumberField
            error={errors.counterRefPrice}
            label={t(
              'page:adminApprovalFlow.autoBid.modal.intraday.counterRefPrice',
            )}
            max={AUTO_BID_MAX_PRICE}
            min={AUTO_BID_MIN_PRICE}
            onChangeNumber={(n) =>
              handleNumberChange('counterRefPriceYenPerKwh', n)
            }
            value={value.counterRefPriceYenPerKwh}
          />

          <AutoBidIntradayNumberField
            error={errors.bidPrice}
            label={
              feeOn
                ? t(
                    'page:adminApprovalFlow.autoBid.modal.intraday.desiredPrice',
                  )
                : t('page:adminApprovalFlow.autoBid.modal.intraday.bidPrice')
            }
            max={AUTO_BID_MAX_PRICE}
            min={AUTO_BID_MIN_PRICE}
            onChangeNumber={(n) => handleNumberChange('bidPriceYenPerKwh', n)}
            value={value.bidPriceYenPerKwh}
          />
          <AutoBidPriceRangeHint
            effective={effective}
            priceRange={priceRange}
          />
        </>
      )}

      <AutoBidIntradayNumberField
        error={errors.adjustment}
        label={t('page:adminApprovalFlow.autoBid.modal.intraday.adjustment')}
        max={AUTO_BID_MAX_ADJUSTMENT}
        min={AUTO_BID_MIN_ADJUSTMENT}
        onChangeNumber={(n) => handleNumberChange('adjustmentYenPerKwh', n)}
        value={value.adjustmentYenPerKwh}
      />
    </div>
  )
}

/**
 * 「ラベル + 数値入力 + 単位」の 1 行と、 その直下の範囲エラー文。
 *
 * エラー文は入力欄と同じ行に置かず縦積みにする (design/CLAUDE.md: エラー文は
 * 入力欄の「下」)。 エラー領域は予約せず、 エラー時だけ行が高くなる。
 * 買/売の 2 組が同時に描画されるため、 aria-describedby の参照先 id は
 * useId でインスタンスごとに分ける。
 */
const AutoBidIntradayNumberField = ({
  label,
  value,
  min,
  max,
  error,
  onChangeNumber,
}: {
  label: string
  value: number
  min: number
  max: number
  error: AutoBidValidationError | undefined
  onChangeNumber: (n: number) => void
}): React.ReactElement => {
  const { t } = useTranslation()
  const errorId = useId()

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <AutoBidFormRow label={label}>
        {({ controlId }) => (
          <>
            <DraftNumberInput
              aria-describedby={error !== undefined ? errorId : undefined}
              aria-invalid={error !== undefined}
              id={controlId}
              inputWidth="96px"
              max={max}
              min={min}
              onChangeNumber={onChangeNumber}
              step={0.01}
              value={value}
            />
            <Text color="Text.midEmphasis">
              {t('page:adminApprovalFlow.autoBid.modal.priceUnit')}
            </Text>
          </>
        )}
      </AutoBidFormRow>
      {error !== undefined && (
        <div
          id={errorId}
          style={{ fontSize: fontSizes.sm.value, color: eclColor.negative }}
        >
          {t(AUTO_BID_VALIDATION_MESSAGE_KEY[error])}
        </div>
      )}
    </div>
  )
}

/**
 * 下書き文字列 → モデル値。 数値化できない途中値 (空・`-` だけ) は NaN。
 * NaN は `validateAutoBidPrice` / `validateAutoBidAdjustment` が範囲エラーと
 * して扱う (#5164)。
 */
function draftToNumber(draft: string): number {
  return draft.trim() === '' ? Number.NaN : Number(draft)
}

/** clamp 欄 (範囲エラーを持たない欄) の値確定。 途中値は下限値。 */
function clampDraft(n: number, min: number, max: number): number {
  return Number.isNaN(n) ? min : Math.max(min, Math.min(max, n))
}

type DraftNumberInputProps = {
  /**
   * `AutoBidFormRow` に載せる場合は**書かない** (ラベルとの関連付けは `id` が
   * 持つ)。 Row の外で使う場合だけ指定する。
   */
  'aria-label'?: string
  'aria-describedby'?: string
  'aria-invalid'?: boolean
  /** `AutoBidFormRow` が配る `controlId`。 `<label for>` と入力を結ぶ。 */
  id?: string
  value: number
  onChangeNumber: (n: number) => void
  min: number
  max: number
  step: number
  /**
   * 範囲外をエラー表示でなく clamp で扱う欄 (= カバー率) 用。 clamp した場合は
   * 表示も clamp 後の値へ合わせる (例: 100 に `0` を足して 1000 と見えるのに
   * 100 が保存される乖離を防ぐ)。
   */
  clampToRange?: boolean
  /** 入力欄の幅 (Chakra style prop の `width` とは別。 style へ渡す)。 */
  inputWidth: string
}

/**
 * 表示用の下書き文字列を保持する数値入力欄。
 *
 * clamp / NaN→0 済みの数値を controlled な `type="number"` に直接束縛すると、
 * 数値として解釈できない中間状態が潰される。 とくに `-` だけを打った瞬間は
 * `e.target.value` が `''` になるためモデルが 0 になり、 React が「value===0
 * かつ DOM が空」の DOM を `"0"` へ書き戻すので、 打った `-` が消えて負値に
 * 到達できない (調整欄は -999〜999 が正常値)。
 *
 * 表示は生文字列で保持し (空は空のまま見せる)、 数値化できない途中値は
 * モデルへ NaN として渡す。 `validateAutoBidPrice` / `validateAutoBidAdjustment`
 * が NaN を範囲エラー扱いするので (#5164)、 空欄のまま保存して 0 などの
 * 意図しない値で上書きされることはない。 ただし clamp 欄 (= カバー率) は
 * 範囲エラーを持たず途中値を下限値として保存してしまうので、
 * その場合は表示も下限値へ正規化して表示と保存値を一致させる。
 * 値が 0 でも DOM を `"0"` へ書き戻さないのが下書き state の目的
 * (AutoBidEditModal のカバー率欄と同じパターン)。 下書きは呼び出し側が
 * モーダルを開く度に remount して破棄する (blur では正規化しない。 modal の
 * focus trap による blur で入力中の下書きが消えるため)。
 * この 2 モーダルは React Hook Form + Zod ベースではないため、 局所的な
 * 下書き state で扱う。
 */
const DraftNumberInput = ({
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
  id,
  value,
  onChangeNumber,
  min,
  max,
  step,
  clampToRange = false,
  inputWidth,
}: DraftNumberInputProps): React.ReactElement => {
  const [text, setText] = useState(String(value))

  // モデル値が外部要因 (モーダル reset / clamp) で変わった時だけ下書きを追従。
  // 編集中の下書きが既に同じモデル値へ round-trip する間は上書きしない
  // (ここで途中値を数字へ上書きすると打った `-` が消える)。
  useEffect(() => {
    setText((prev) =>
      Object.is(draftToNumber(prev), value) ? prev : String(value),
    )
  }, [value])

  const handleChange = (raw: string): void => {
    const n = draftToNumber(raw)
    const next = clampToRange ? clampDraft(n, min, max) : n
    // clamp された入力は表示も clamp 後の値へ揃える (表示と保存値の乖離防止)。
    setText(Object.is(next, n) ? raw : String(next))
    onChangeNumber(next)
  }

  return (
    <Input
      aria-describedby={ariaDescribedBy}
      aria-invalid={ariaInvalid}
      aria-label={ariaLabel}
      id={id}
      max={max}
      min={min}
      onChange={(e) => handleChange(e.target.value)}
      step={step}
      style={{ width: inputWidth }}
      type="number"
      value={text}
    />
  )
}
